/** Talking to the API. Same origin, session cookie, one error shape. */

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const e = json?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'error', e.message ?? `Request failed (${res.status})`);
  }
  return json as T;
}

export const get = <T>(p: string) => call<T>('GET', p);
export const post = <T>(p: string, b?: unknown) => call<T>('POST', p, b ?? {});
export const patch = <T>(p: string, b: unknown) => call<T>('PATCH', p, b);
export const put = <T>(p: string, b: unknown) => call<T>('PUT', p, b);
export const del = <T>(p: string) => call<T>('DELETE', p);

/** A file, straight from the drop zone. */
export async function uploadFile(accountId: string, file: File) {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await fetch(`/api/v1/accounts/${accountId}/imports`, {
    method: 'POST', credentials: 'same-origin', body: form,
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const e = json?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'error', e.message ?? 'That file could not be read.');
  }
  return json as {
    kind: string; rows: number; duplicate: boolean;
    period_start: string | null; period_end: string | null; warnings: string[];
  };
}

export function money(v: number | string | null | undefined, currency = 'INR') {
  const n = Number(v ?? 0);
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
}

export function day(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
